import {useEffect, useRef} from 'react';

import useFilters from '@/components/features/search/filters/FiltersUtils/useFilters';
import {useEventBarV2} from '../EventBarV2Provider';
import {isStagedFilterActive} from '../utils/what';
import useFilterSearchSignature from './useFilterSearchSignature';

// Keeps the What input in step with the committed search. `hasUserEditedWhatRef` means "diverged
// from the committed search", not "ever typed". An uncommitted edit (whatValue !== keyword, no
// active staged filter) is left untouched until either the search catches up with it or a filter
// applied elsewhere on the page runs a search of its own.
const useSyncWhatWithFilters = (): void => {
  const {
    whatValue,
    setWhatValue,
    setWhatSelection,
    stagedSearchFilter,
    setStagedSearchFilter,
    hasUserEditedWhatRef,
  } = useEventBarV2();

  const {filters} = useFilters();
  const {keyword} = filters;

  const stagedFilterActive = stagedSearchFilter
    ? isStagedFilterActive(stagedSearchFilter, filters)
    : false;

  const filtersSignature = useFilterSearchSignature();
  const previousFiltersSignatureRef = useRef(filtersSignature);

  useEffect(() => {
    const filtersChanged = previousFiltersSignatureRef.current !== filtersSignature;
    previousFiltersSignatureRef.current = filtersSignature;

    if (hasUserEditedWhatRef.current) {
      // Resume mirroring once the input is back in step with the committed search — or once a
      // filter triggers a new search, since typed-but-unsubmitted text was never part of it.
      const backInSync = stagedSearchFilter ? stagedFilterActive : whatValue === keyword;
      if (!backInSync && !filtersChanged) return;
      hasUserEditedWhatRef.current = false;
    }

    // A suggested search: drop the chip (and its selection) once its filter is no longer active.
    if (stagedSearchFilter) {
      if (!stagedFilterActive) {
        setStagedSearchFilter(null);
        setWhatSelection(null);
        setWhatValue('');
      }
      return;
    }

    // A keyword search: mirror the committed keyword, emptying the input when it's cleared.
    if (whatValue !== keyword) setWhatValue(keyword);
  }, [
    keyword,
    filtersSignature,
    stagedSearchFilter,
    stagedFilterActive,
    whatValue,
    setWhatValue,
    setWhatSelection,
    setStagedSearchFilter,
    hasUserEditedWhatRef,
  ]);
};

export default useSyncWhatWithFilters;
